import { Box, Button, Divider, InputLabel, Stack, Tab, Tabs } from "@mui/material";
import React, { useMemo, useState } from "react";

import { TabPanel } from "components/common/TabPanel";
import { TextInput } from "components/common/TextInput";
import { FirstAnswerTime } from "components/detector/FirstAnswerTime";
import GroupSelection from "components/detector/GroupSelection";
import { NotesModal } from "components/detector/NotesModal/NotesModal";
import { DEFAULT_PATIENCE_TIME, STABLE_DETECTOR_MODES } from "config/constants";
import { ConfidenceSlider } from "features/confidenceSlider/ConfidenceSlider";
import { getTotalClasses } from "features/confidenceSlider/utils";
import { useAnnotations } from "hooks/DetectorDetail/useAnnotations";
import { DetectorTabs, useDetectorDetailSettings } from "hooks/DetectorDetail/useDetectorDetailSettings";
import { useAppDispatch } from "hooks/store";
import { ConfirmDeleteModal } from "new-components/modals/ConfirmDeleteModal";
import { Typography } from "new-components/typography/Typography";
import { handleResetNotes } from "store/slices/Notes/notesSlice";
import { DetectorModes, DetectorModesReadable } from "types/createDetector";
import { Detector } from "types/detectors";
import { User } from "types/users";

import { AnnotationsPanel } from "./components/AnnotationsPanel";
import { CountDetectorDetailSettings } from "./components/CountDetectorDetailSettings";
import { DetectorControls } from "./components/DetectorControls";
import { MultichoiceDetailSettings } from "./components/MultichoiceDetectorDetailSettings";
import { TabCodeContent } from "./components/TabCodeContent";
import { TextRecognitionDetailSettings } from "./components/TextRecognitionDetailSettings";

interface DetectorDetailSettingsProps {
  detector: Detector;
  updateDetector: (arg: any) => Promise<Detector>;
  getDetector: () => Promise<Detector>;
  user?: User;
  isDetectorUpdating: boolean;
  detectorFetchData?: Detector;
  fetchDetectorError?: Error;
}

const MODES_WITHOUT_CONFIDENCE_THRESHOLD = [DetectorModes.TEXT_RECOGNITION];

export function DetectorDetailSettings(props: DetectorDetailSettingsProps) {
  const { detector, getDetector, updateDetector, user, isDetectorUpdating, detectorFetchData, fetchDetectorError } =
    props;

  const {
    deleteModalOpen,
    handleRemoveInput,
    handleSwitchAnnotations,
    annotations,
    enableAnnotations,
    setAnnotations,
    setDeleteModalOpen,
    setSelectedAnnotation,
    updateAnnotationsFunc,
  } = useAnnotations({
    detectorFetchData,
    fetchDetectorError,
    updateDetector,
  });

  const hasImageQueries = detector.last_image_query !== null;

  const { editableDetectorFields, handleTabsChange, onConfidenceThresholdCommitted, value, setEditableDetectorFields } =
    useDetectorDetailSettings({
      detectorFetchData,
      fetchDetectorError,
      updateDetector,
      getDetector,
      hasImageQueries,
    });
  const [openNotesModal, setOpenNotesModal] = useState<boolean>(false);

  const evaluationResults = useMemo(() => detector?.evaluation_results, [detector?.evaluation_results]);
  const showAnnotationsOption = detector.mode === DetectorModes.BINARY && user?.allow_annotations;

  const dispatch = useAppDispatch();

  const handleOpenNotes = () => {
    setOpenNotesModal(true);
  };
  const handleCloseNotes = () => {
    setOpenNotesModal(false);
    dispatch(handleResetNotes());
  };

  return (
    <>
      <Tabs
        value={value}
        onChange={handleTabsChange}
        aria-label="Posicheck Preview Tabs"
        variant="fullWidth"
        className="text-black"
      >
        <Tab label={<Typography>Escalation Criteria</Typography>} value={DetectorTabs.Escalation} />
        <Tab label={<Typography>Setup</Typography>} value={DetectorTabs.Setup} />
        <Tab label={<Typography>Code</Typography>} value={DetectorTabs.Code} />
      </Tabs>
      <TabPanel value={value} id={DetectorTabs.Escalation}>
        {!MODES_WITHOUT_CONFIDENCE_THRESHOLD.includes(detector.mode) && (
          <ConfidenceSlider
            value={editableDetectorFields?.confidence_threshold}
            setValue={(value: any) =>
              setEditableDetectorFields({ ...editableDetectorFields, confidence_threshold: value })
            }
            setValueCommitted={(value: number) => onConfidenceThresholdCommitted(value)}
            evaluationResults={evaluationResults}
            hideTradeoffCharts={detector.mode !== DetectorModes.BINARY}
            totalClasses={getTotalClasses(detector)}
          />
        )}
        <FirstAnswerTime
          firstAnswerTimeValue={detector.patience_time || DEFAULT_PATIENCE_TIME}
          detectorId={detector.id}
          refetchData={getDetector}
        />
        <DetectorControls
          cloudLabelingValue={detector?.escalation_type || "STANDARD"}
          updateDetector={updateDetector}
          refetchData={getDetector}
        />
      </TabPanel>
      <TabPanel value={value} id={DetectorTabs.Setup}>
        <Stack spacing={2} divider={<Divider />} className="pt-2 pb-4 mb-2 border-b-2 border-neutral-400">
          <Box>
            <InputLabel className="uppercase text-sm text-neutral-700 font-bold mb-2">Name:</InputLabel>
            <TextInput value={detector.name} disabled fullWidth />
          </Box>
          {!detector.is_explore_detector && (
            <GroupSelection
              editableDetectorFields={editableDetectorFields}
              setEditableDetectorFields={setEditableDetectorFields}
              detectorFetchData={detector}
              updateDetector={updateDetector}
              getDetector={getDetector}
            />
          )}

          <Box>
            <InputLabel className="uppercase text-sm text-neutral-700 font-bold mb-2">Mode:</InputLabel>
            <Box
              className="text-neutral-medium rounded px-[14px] flex gap-2 items-center h-10"
              sx={{
                border: "1px solid #B3B6B6",
                borderRadius: "4px",
              }}
            >
              <Typography>{DetectorModesReadable[detector.mode]}</Typography>
              {!STABLE_DETECTOR_MODES.includes(detector.mode) && (
                <Typography variant="small" className="-mt-1">
                  (BETA)
                </Typography>
              )}
            </Box>
          </Box>

          <Box>
            <InputLabel className="uppercase text-sm text-neutral-700 font-bold mb-2">Notes:</InputLabel>

            <Button
              onClick={handleOpenNotes}
              className="flex justify-center w-full px-2 py-1 gap-4 items-center rounded-md transition duration-100 ease-in shadow-md text-black bg-[#E5E9ED] hover:bg-[#15121A] hover:text-[#FFF]"
            >
              <Typography noWrap textTransform="capitalize">
                Detector Notes
              </Typography>
            </Button>

            {openNotesModal && (
              <NotesModal
                detector={detector}
                ownerGroupId={detector.owner?.id}
                ownerGroupName={detector.owner?.name}
                openModal={openNotesModal}
                onClose={handleCloseNotes}
              />
            )}
          </Box>
        </Stack>
        <Divider className="mb-4" />
        {showAnnotationsOption && (
          <AnnotationsPanel
            enableAnnotations={enableAnnotations}
            annotations={annotations}
            setAnnotations={setAnnotations}
            switchAnnotations={handleSwitchAnnotations}
            updateAnnotationsFunc={updateAnnotationsFunc}
            loading={isDetectorUpdating}
            setDeleteModalOpen={setDeleteModalOpen}
            setSelectedAnnotation={setSelectedAnnotation}
          />
        )}

        {detector.mode === DetectorModes.MULTICHOICE && (
          <MultichoiceDetailSettings choices={detector.mode_configuration?.class_names || []} />
        )}

        {detector.mode === DetectorModes.COUNT && (
          <CountDetectorDetailSettings
            countingObjectClass={annotations[0]}
            maxCount={detector.mode_configuration?.max_count}
          />
        )}

        {detector.mode === DetectorModes.TEXT_RECOGNITION && (
          <TextRecognitionDetailSettings characterLimit={detector.mode_configuration?.value_max_length} />
        )}
      </TabPanel>
      <TabPanel value={value} id={DetectorTabs.Code}>
        <TabCodeContent detectorId={detector.id} />
      </TabPanel>
      {deleteModalOpen && (
        <ConfirmDeleteModal
          isDeleteModalOpen={deleteModalOpen}
          modalContent="<strong>Are you sure you want to delete this desired annotation?</strong><br />"
          modalTitle="Delete Desired Annotation"
          setIsDeleteModalOpen={setDeleteModalOpen}
          onRemoveHandler={handleRemoveInput}
        />
      )}
    </>
  );
}

export default DetectorDetailSettings;
